import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import styled from 'styled-components';
import SectionHeading from '../../components/common/SectionHeading';
import useApi from '../../hooks/useApi';

const PORTAL_USER_KEY = 'portal_user';
const BACKEND_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5003';

const Wrapper = styled.section`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 1.5rem;
  padding: 2rem;
  box-shadow: ${({ theme }) => theme.shadows.md};

  @media (max-width: 900px) {
    border-radius: 1rem;
    padding: 1rem;
  }
`;

const BackLink = styled(Link)`
  display: inline-block;
  margin-bottom: 1rem;
  font-size: 0.88rem;
  font-weight: 700;
  color: #4f46e5;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const EventIntro = styled.p`
  margin: 0.5rem 0 0;
  color: #475569;
  font-size: 0.92rem;
  line-height: 1.55;
`;

const EventDate = styled.p`
  margin: 0.35rem 0 0;
  color: #64748b;
  font-size: 0.84rem;
`;

const VideoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.25rem;
  margin-top: 1.5rem;
`;

const VideoCard = styled(Link)`
  display: flex;
  flex-direction: column;
  border-radius: 1rem;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  background: #fff;
  text-decoration: none;
  color: inherit;
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }
`;

const ThumbWrap = styled.div`
  position: relative;
  padding-bottom: 56.25%;
  background: #0f172a;
  overflow: hidden;

  img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const PlayBadge = styled.span`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 3rem;
  height: 3rem;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.72);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  pointer-events: none;
`;

const CardBody = styled.div`
  padding: 0.9rem 1rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

const WorkTitle = styled.h3`
  margin: 0;
  font-size: 0.98rem;
  font-weight: 800;
  color: #0f172a;
  line-height: 1.35;
`;

const StudentName = styled.p`
  margin: 0;
  font-size: 0.82rem;
  color: #6366f1;
  font-weight: 700;
`;

const StatsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
  margin-top: 0.15rem;
  font-size: 0.78rem;
  color: #64748b;
  font-variant-numeric: tabular-nums;
`;

const ErrorText = styled.p`
  margin: 1rem 0 0;
  color: #b91c1c;
  font-size: 0.9rem;
`;

const EmptyText = styled.p`
  margin: 1.1rem 0 0;
  color: #64748b;
  font-size: 0.9rem;
`;

const readPortalUser = () => {
  try {
    const raw = sessionStorage.getItem(PORTAL_USER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && parsed.login_id ? parsed : null;
  } catch {
    return null;
  }
};

const formatEventDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' });
};

const PortalPresentationEventPage = () => {
  const { eventId, eventYear } = useParams();
  const { request, loading } = useApi();
  const portalUser = useMemo(() => readPortalUser(), []);
  const [resolvedEventId, setResolvedEventId] = useState(null);
  const [event, setEvent] = useState(null);
  const [works, setWorks] = useState([]);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!portalUser?.login_id) return;
    setError('');
    try {
      const qs = new URLSearchParams({ login_id: portalUser.login_id });
      let targetEventId = eventId;
      if (!targetEventId && eventYear) {
        const listData = await request({
          endpoint: `${BACKEND_BASE_URL}/api/presentation-events?${qs.toString()}`,
        });
        const matched = (listData?.events || []).find((ev) => (ev?.title || '').includes(String(eventYear)));
        targetEventId = matched?.id ? String(matched.id) : '';
      }
      if (!targetEventId) {
        setEvent(null);
        setWorks([]);
        setError('발표회를 찾을 수 없습니다.');
        return;
      }
      const data = await request({
        endpoint: `${BACKEND_BASE_URL}/api/presentation-events/${encodeURIComponent(targetEventId)}?${qs.toString()}`,
      });
      setResolvedEventId(targetEventId);
      setEvent(data?.event || null);
      setWorks(data?.works || []);
    } catch (e) {
      setResolvedEventId(null);
      setEvent(null);
      setWorks([]);
      setError(e?.responseData?.message || '발표회를 불러오지 못했습니다.');
    }
  }, [eventId, eventYear, portalUser?.login_id, request]);

  useEffect(() => {
    load();
  }, [load]);

  if (!event && !error) {
    return (
      <Wrapper>
        <BackLink to="/portal/home">← 포털 홈</BackLink>
        <SectionHeading eyebrow="PRESENTATION" title="발표회" />
        <p style={{ color: '#64748b' }}>{loading ? '불러오는 중...' : ''}</p>
      </Wrapper>
    );
  }

  if (error && !event) {
    return (
      <Wrapper>
        <BackLink to="/portal/home">← 포털 홈</BackLink>
        <SectionHeading eyebrow="PRESENTATION" title="발표회" />
        <ErrorText>{error}</ErrorText>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <BackLink to="/portal/home">← 포털 홈</BackLink>
      <SectionHeading eyebrow="PRESENTATION" title={event.title} />
      {event.event_date ? <EventDate>{formatEventDate(event.event_date)}</EventDate> : null}
      {event.description ? <EventIntro>{event.description}</EventIntro> : null}

      {works.length > 0 ? (
        <VideoGrid>
          {works.map((work) => (
            <VideoCard
              key={work.id}
              to={`/portal/presentations/${resolvedEventId || eventId}/works/${work.id}`}
            >
              <ThumbWrap>
                {work.youtube_video_id ? (
                  <img
                    src={`https://img.youtube.com/vi/${work.youtube_video_id}/hqdefault.jpg`}
                    alt=""
                    loading="lazy"
                  />
                ) : null}
                <PlayBadge aria-hidden>▶</PlayBadge>
              </ThumbWrap>
              <CardBody>
                <WorkTitle>{work.title}</WorkTitle>
                {work.student_name ? <StudentName>{work.student_name}</StudentName> : null}
                <StatsRow>
                  <span>♥ {work.like_count ?? 0}</span>
                  <span>댓글 {work.comment_count ?? 0}</span>
                  <span>조회 {work.view_count ?? 0}</span>
                </StatsRow>
              </CardBody>
            </VideoCard>
          ))}
        </VideoGrid>
      ) : (
        <EmptyText>작품 상세 영상은 재생목록에서 확인해 주세요.</EmptyText>
      )}
    </Wrapper>
  );
};

export default PortalPresentationEventPage;
